// API utilities - usando Supabase diretamente + Shopify API

import { supabase } from '../config/supabase';
import { Review } from '../types';
import { getShopAccessToken, getShopDomain, verifyPurchase } from './shopifyApi';

export interface ApiResponse<T> {
  data?: T;
  error?: string;
  message?: string;
}

// Função helper para requests diretos no Supabase
export async function supabaseRequest<T>(
  table: string,
  options: {
    shopId?: string;
    filters?: Record<string, any>;
    orderBy?: { column: string; ascending?: boolean };
  } = {}
): Promise<ApiResponse<T[]>> {
  try {
    // Em desenvolvimento local com shopId mock, retornar vazio
    if (options.shopId === 'local-dev-shop-id') {
      return { data: [] };
    }

    if (!options.shopId) {
      return { data: [] };
    }

    let query = supabase.from(table).select('*');

    if (options.shopId) {
      query = query.eq('shop_id', options.shopId);
    }

    if (options.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        query = query.eq(key, value);
      });
    }

    if (options.orderBy) {
      query = query.order(options.orderBy.column, {
        ascending: options.orderBy.ascending ?? false,
      });
    }

    const { data, error } = await query;

    if (error) {
      throw error;
    }

    return { data: data as T[] };
  } catch (error) {
    return {
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

// Helper para verificar se shopId é válido (não é mock de dev)
const isValidShopId = (shopId: string): boolean => {
  return shopId !== 'local-dev-shop-id';
};

// Função para mapear dados do Supabase (snake_case) para o tipo Review (camelCase)
const mapSupabaseReviewToReview = (dbReview: any): Review => {
  return {
    id: dbReview.id,
    productId: dbReview.product_id || dbReview.productId,
    productTitle: dbReview.product_title || dbReview.productTitle,
    customerName: dbReview.customer_name || dbReview.customerName,
    customerEmail: dbReview.customer_email || dbReview.customerEmail,
    rating: dbReview.rating,
    title: dbReview.title || null,
    comment: dbReview.comment,
    photos: Array.isArray(dbReview.photos) ? dbReview.photos : (dbReview.photos ? [dbReview.photos] : []),
    verifiedPurchase: dbReview.verified_purchase || dbReview.verifiedPurchase || false,
    createdAt: dbReview.created_at || dbReview.createdAt,
    updatedAt: dbReview.updated_at || dbReview.updatedAt,
    status: dbReview.status,
  };
};

// API functions usando Supabase diretamente
export const reviewsApi = {
  getAll: async (shopId: string, filters?: { status?: string; productId?: string }) => {
    if (!isValidShopId(shopId)) {
      return { data: [] };
    }
    
    const supabaseFilters: Record<string, any> = {};
    if (filters?.status) supabaseFilters.status = filters.status;
    if (filters?.productId) supabaseFilters.product_id = filters.productId;
    
    const result = await supabaseRequest<any>('reviews', {
      shopId,
      filters: supabaseFilters,
      orderBy: { column: 'created_at', ascending: false },
    });
    
    if (result.error) {
      return result;
    }
    
    return {
      data: result.data?.map(mapSupabaseReviewToReview) || [],
    };
  },
  
  getById: async (id: string, shopId: string) => {
    if (!isValidShopId(shopId)) {
      return { error: 'Invalid shop ID for local development' };
    }
    
    const { data, error } = await supabase
      .from('reviews')
      .select('*')
      .eq('id', id)
      .eq('shop_id', shopId)
      .single();
    
    if (error) return { error: error.message };
    if (!data) return { error: 'Review not found' };
    
    return { data: mapSupabaseReviewToReview(data) };
  },
  
  approve: async (id: string, shopId: string) => {
    if (!isValidShopId(shopId)) {
      return { error: 'Invalid shop ID for local development' };
    }
    
    const { data, error } = await supabase
      .from('reviews')
      .update({ status: 'approved' })
      .eq('id', id)
      .eq('shop_id', shopId)
      .select()
      .single();
    
    if (error) return { error: error.message };
    if (!data) return { error: 'Review not found' };
    
    return { data: mapSupabaseReviewToReview(data) };
  },
  
  reject: async (id: string, shopId: string) => {
    if (!isValidShopId(shopId)) {
      return { error: 'Invalid shop ID for local development' };
    }
    
    const { data, error } = await supabase
      .from('reviews')
      .update({ status: 'rejected' })
      .eq('id', id)
      .eq('shop_id', shopId)
      .select()
      .single();
    
    if (error) return { error: error.message };
    if (!data) return { error: 'Review not found' };
    
    return { data: mapSupabaseReviewToReview(data) };
  },
  
  delete: async (id: string, shopId: string) => {
    if (!isValidShopId(shopId)) {
      return { error: 'Invalid shop ID for local development' };
    }
    
    const { error } = await supabase
      .from('reviews')
      .delete()
      .eq('id', id)
      .eq('shop_id', shopId);
    
    if (error) return { error: error.message };
    return { data: { success: true } };
  },
  
  create: async (review: {
    shopId: string;
    productId: string;
    productTitle: string;
    customerName: string;
    customerEmail: string;
    rating: number;
    title?: string;
    comment: string;
    verifiedPurchase?: boolean;
  }) => {
    if (!isValidShopId(review.shopId)) {
      return { error: 'Invalid shop ID for local development' };
    }
    
    const { data, error } = await supabase
      .from('reviews')
      .insert({
        shop_id: review.shopId,
        product_id: review.productId,
        product_title: review.productTitle,
        customer_name: review.customerName,
        customer_email: review.customerEmail,
        rating: review.rating,
        title: review.title || null,
        comment: review.comment,
        verified_purchase: review.verifiedPurchase || false,
        status: 'pending', // Sempre começa como pendente
      })
      .select()
      .single();
    
    if (error) return { error: error.message };
    if (!data) return { error: 'Failed to create review' };
    
    return { data: mapSupabaseReviewToReview(data) };
  },
};

export const statsApi = {
  getStats: async (shopId: string) => {
    if (!isValidShopId(shopId)) {
      return {
        data: {
          total_reviews: 0,
          average_rating: 0,
          pending_reviews: 0,
          products_with_reviews: 0,
          rating_distribution: { '5': 0, '4': 0, '3': 0, '2': 0, '1': 0 },
        },
      };
    }
    
    // Buscar reviews aprovadas
    const { data: reviews, error } = await supabase
      .from('reviews')
      .select('rating, status')
      .eq('shop_id', shopId);
    
    if (error) return { error: error.message };
    
    const approvedReviews = reviews?.filter(r => r.status === 'approved') || [];
    const totalReviews = approvedReviews.length;
    const averageRating = totalReviews > 0
      ? approvedReviews.reduce((sum, r) => sum + r.rating, 0) / totalReviews
      : 0;
    
    const pendingCount = reviews?.filter(r => r.status === 'pending').length || 0;
    
    // Contar produtos únicos com reviews
    const { data: productsData } = await supabase
      .from('reviews')
      .select('product_id')
      .eq('shop_id', shopId)
      .eq('status', 'approved');
    
    const uniqueProducts = new Set(productsData?.map(r => r.product_id) || []);
    
    // Distribuição de ratings
    const ratingDistribution = { '5': 0, '4': 0, '3': 0, '2': 0, '1': 0 };
    approvedReviews.forEach(r => {
      const key = r.rating.toString() as keyof typeof ratingDistribution;
      if (key in ratingDistribution) ratingDistribution[key]++;
    });
    
    return {
      data: {
        total_reviews: totalReviews,
        average_rating: Math.round(averageRating * 10) / 10,
        pending_reviews: pendingCount,
        products_with_reviews: uniqueProducts.size,
        rating_distribution: ratingDistribution,
      },
    };
  },
};

export const settingsApi = {
  get: async (shopId: string) => {
    const defaults = {
      auto_approve: false,
      require_verification: true,
      email_notifications: true,
      min_comment_length: 10,
      banned_words: '',
      auto_reject_rating: '0',
      review_order: 'newest',
      reviews_per_page: 10,
      show_photos: true,
      show_verified_badge: true,
      show_review_title: true,
      title_required: false,
      allow_photo_upload: true,
      max_photos: 3,
      max_photo_size: 5,
      shop_id: shopId,
    };

    if (!isValidShopId(shopId)) {
      return { data: defaults };
    }
    
    const { data, error } = await supabase
      .from('shops')
      .select('settings')
      .eq('id', shopId)
      .single();
    
    if (error) return { error: error.message };
    return { data: { ...defaults, ...data.settings, shop_id: shopId } };
  },
  
  update: async (shopId: string, settings: any) => {
    if (!isValidShopId(shopId)) {
      return { error: 'Invalid shop ID for local development' };
    }
    
    const { data, error } = await supabase
      .from('shops')
      .update({ settings })
      .eq('id', shopId)
      .select('settings')
      .single();
    
    if (error) return { error: error.message };
    return { data: data.settings };
  },
};

// API para verificar compra (usando Shopify API)
export const verificationApi = {
  verifyPurchase: async (shopId: string, customerEmail: string, productId: string) => {
    const shopDomain = await getShopDomain(shopId);
    const accessToken = await getShopAccessToken(shopId);

    if (!shopDomain || !accessToken) {
      return { error: 'Shop not found or not authenticated' };
    }

    return verifyPurchase(shopDomain, accessToken, customerEmail, productId);
  },
};

